import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';

import Description from '../../components/Description/Description';
import Group from '../../components/Group/Group';
import IconWithText from '../../components/IconWithText/IconWithText';
import MonetaryField from '../../components/MonetaryField/MonetaryField';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import { ValidationResultType } from '../../hooks/useFieldValidation';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldValue } from '../../store/applicationform/applicationFormSelectors';
import { WizardPageAction } from '../../store/wizard/wizard';

/**
 * NO-only despite the missing `NO` suffix (LP-2862 dropped it when this page still served DK too). The
 * `cl-common` PCN-620 rescope (LP-2971) moved this page's two fields -- `coapplicantYearlyGrossIncome` and
 * `coapplicantMonthlyIncomeOther` -- into NO's own exclusive contract block; DK no longer declares either
 * one. DK's co-applicant wizard uses `CoApplicantFinancePageDK` instead. Do not reuse this page for DK again
 * without first checking whether its fields are still NO-only.
 */
const CoApplicantIncomePage = () => {
    // Deliberately not 'CoApplicantIncomePage'. 'FinancePage' is the historical analytics name for the income
    // step across the whole app (IncomePageNO, IncomePageSE and FinancePageDK all report it), and this label
    // predates LP-2862's rename. Changing it would break GTM/APM dashboard continuity for NO.
    useComponentTracker('CoApplicantFinancePage');

    const dispatch = useAppDispatch();
    const translate = useTranslation();
    const { coApplicantTotalSections, coApplicantTotalAboutYouPages } = usePagesConfig();
    const defaultPagesAction = useDefaultPagesAction();
    const validationErrors = useValidation(['coapplicantYearlyGrossIncome']);
    const { currentPage } = usePagesContext();
    const employmentType = useAppSelector(selectFieldValue)('coapplicantEmploymentType');
    // NO and DK now share the same PENSIONER id (see OccupationField), so a plain equality check is enough;
    // this mirrors what FinancePageDK does for the main applicant.
    const isPensioner = employmentType === 'PENSIONER';

    useSetBannerType('bannerOne');

    const minValueValidation = async (value?: string): Promise<ValidationResultType> => {
        const isValid = (value ?? 0) > '0';

        return {
            valid: isValid,
            errorMessageKey: isValid ? undefined : translate('field.applicant.min-value.error'),
        };
    };

    const onAction = async (action: WizardPageAction) => {
        if (action === 'next' && validationErrors.length) {
            scrollToFieldWithError(validationErrors);
            validationErrors.forEach((validationError) => {
                dispatch(addOrUpdateFormField(validationError));
            });
            return false;
        }

        return defaultPagesAction(action);
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={onAction}
                currentPage={currentPage}
                currentSection={0}
                numberOfSections={coApplicantTotalSections}
                numberOfPagesInSection={coApplicantTotalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
            <Title text={translate('page.income.title')} />
            <Description text={translate('page.income.description')} />
            <Group>
                <MonetaryField
                    name="coapplicantYearlyGrossIncome"
                    label={translate('field.applicant-yearly-gross-income.label')}
                    customValidation={minValueValidation}
                />
                <MonetaryField
                    name="coapplicantMonthlyIncomeOther"
                    label={translate('field.applicant-monthly-income-other.label')}
                />
            </Group>
            {isPensioner ? (
                <IconWithText icon={faCircleExclamation} text="page.income.body-text" iconSize="sm" />
            ) : null}
            <PagesFooter
                actions={['next']}
                onAction={onAction}
                currentSection={0}
                numberOfSections={coApplicantTotalSections}
                numberOfPagesInSection={coApplicantTotalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
        </>
    );
};

export default CoApplicantIncomePage;
